import { useState } from 'react';
import type { MutableRefObject } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Col, FormGroup, InputGroup, InputGroupAddon, InputGroupText, Label, Row } from 'reactstrap';
import ReactDatetime from 'react-datetime';
import EmployeeService from '../../../../services/Employee';
import PutOnLeaveForm from '../PutOnLeaveForm';
import confirmAlertWithForm from '../../../../helpers/confirmAlertWithForm';
import LeaveOfAbsenceRecordsModal from './LeaveOfAbsenceRecordsModal';

interface UsePutOnLeaveModalParams {
  token: string;
  currentSchool: any;
  loadList: () => void;
}

const EndLoaActualLastDayPicker = ({ dateRef }: { dateRef: MutableRefObject<string> }) => {
  const [value, setValue] = useState(() => moment(dateRef.current, 'MM/DD/YYYY'));

  return (
    <ReactDatetime
      inputProps={{
        placeholder: 'Select Actual Last day of LOA',
        required: true,
      }}
      onChange={(newValue) => {
        if (moment.isMoment(newValue)) {
          setValue(newValue);
          dateRef.current = newValue.format('MM/DD/YYYY');
        } else if (typeof newValue === 'string') {
          const parsed = moment(newValue, 'MM/DD/YYYY', true);
          if (parsed.isValid()) {
            setValue(parsed);
            dateRef.current = parsed.format('MM/DD/YYYY');
          } else {
            dateRef.current = newValue;
          }
        }
      }}
      timeFormat={false}
      value={value}
      dateFormat={'MM/DD/YYYY'}
      closeOnSelect={true}
    />
  );
};

export const usePutOnLeaveModal = ({ token, currentSchool, loadList }: UsePutOnLeaveModalParams) => {
  const [isPutOnLeaveFormOpen, setIsPutOnLeaveFormOpen] = useState(false);
  const [putOnLeaveStep, setPutOnLeaveStep] = useState(0);
  const [loaUser, setLoaUser] = useState<any>();
  const [firstDayOfLoa, setFirstDayOfLoa] = useState(moment().format('MM/DD/YYYY'));
  const [lastDayOfLoa, setLastDayOfLoa] = useState(moment().format('MM/DD/YYYY'));
  const [typeOfLoa, setTypeOfLoa] = useState('');
  const [selectedLoaPolicy, setSelectedLoaPolicy] = useState<any>(null);
  const [selectedLoaReason, setSelectedLoaReason] = useState('');
  const [reasonForLoa, setReasonForLoa] = useState('');
  const [documents, setDocuments] = useState<File[]>([]);
  const [additionalNotes, setAdditionalNotes] = useState('');

  const [confirmEndLoaModalOpen, setConfirmEndLoaModalOpen] = useState(false);
  const [confirmEndLoaData, setConfirmEndLoaData] = useState<{
    employeeId: string;
    actualLastDay: string;
    workerName: string;
    loaRecords: any[];
  } | null>(null);
  const [confirmEndLoaLoading, setConfirmEndLoaLoading] = useState(false);

  const tooglePutOnLeaveForm = () => setIsPutOnLeaveFormOpen((prev) => !prev);

  const resetForm = () => {
    setPutOnLeaveStep(0);
    setFirstDayOfLoa(moment().format('MM/DD/YYYY'));
    setLastDayOfLoa(moment().format('MM/DD/YYYY'));
    setTypeOfLoa('');
    setSelectedLoaPolicy(null);
    setSelectedLoaReason('');
    setReasonForLoa('');
    setDocuments([]);
    setAdditionalNotes('');
  };

  const handlePutOnLeave = async (e: React.FormEvent, user: any) => {
    if (!user?.id) return;
    e?.preventDefault();
    setLoaUser(user);
    resetForm();
    tooglePutOnLeaveForm();
  };

  const handleSubmitPutOnLeave = async (event?: React.FormEvent | unknown) => {
    if (event && typeof (event as React.FormEvent).preventDefault === 'function') {
      (event as React.FormEvent).preventDefault();
    }
    if (event && typeof (event as React.FormEvent).preventDefault !== 'function') {
      loadList();
      return;
    }

    if (putOnLeaveStep === 0) {
      setPutOnLeaveStep(1);
      return;
    }

    try {
      const data: any = {
        employeeId: loaUser?.id,
        schoolId: currentSchool.value,
        firstDayOfLoa,
        lastDayOfLoa,
        typeOfLoa,
        loaPolicyId: selectedLoaPolicy?.id,
        selectedReason: selectedLoaReason,
        reason: reasonForLoa,
        additionalNotes,
      };

      if (documents.length > 0) {
        data.documents = documents;
      }

      await EmployeeService(token).putOnLeaveWithDetails(data);

      setPutOnLeaveStep(0);
      tooglePutOnLeaveForm();
      loadList();
      toast.success('Employee put on leave successfully!');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Request error');
    }
  };

  const handleEndPutOnLeave = (e: React.FormEvent, user: any) => {
    if (!user?.id) return;
    e?.preventDefault();
    const id = user?.id || '';
    const dateRef: MutableRefObject<string> = { current: moment().format('MM/DD/YYYY') };

    return confirmAlertWithForm({
      title: 'End Leave of Absence',
      messageType: 'EDIT',
      message: 'Please select the Actual Last day of LOA:',
      buttons: {
        confirm: { label: 'Continue', hidden: false },
        cancel: { label: 'Cancel', hidden: false },
      },
      children: (
        <Row className="mt-3">
          <Col>
            <FormGroup className="form-control-label">
              <Label>Actual Last day of LOA</Label>
              <FormGroup className="custom-datepicker mb-0">
                <InputGroup className="input-group-alternative">
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="ni ni-calendar-grid-58" />
                    </InputGroupText>
                  </InputGroupAddon>
                  <EndLoaActualLastDayPicker dateRef={dateRef} />
                </InputGroup>
              </FormGroup>
            </FormGroup>
          </Col>
        </Row>
      ),
      onSubmit: async () => {
        const actualLastDay = dateRef.current;
        try {
          const response = await EmployeeService(token || '').getPutOnLeaveDetails(id);
          let loaRecords = [];
          if (response?.data?.data) {
            loaRecords = Array.isArray(response.data.data) ? response.data.data : [];
          }

          const workerName = `${user?.user?.firstName || ''} ${user?.user?.lastName || ''}`.trim();

          setConfirmEndLoaData({
            employeeId: id,
            actualLastDay,
            workerName,
            loaRecords,
          });
          setConfirmEndLoaModalOpen(true);
        } catch (error: any) {
          if (error?.response?.status === 404 || error?.response?.status === 400) {
            const workerName = `${user?.user?.firstName || ''} ${user?.user?.lastName || ''}`.trim();
            setConfirmEndLoaData({
              employeeId: id,
              actualLastDay,
              workerName,
              loaRecords: [],
            });
            setConfirmEndLoaModalOpen(true);
            return;
          }
          console.error('Error loading LOA details:', error);
          toast.error('Failed to load LOA details');
        }
      },
    });
  };

  const handleConfirmEndLoa = async () => {
    if (!confirmEndLoaData) return;
    setConfirmEndLoaLoading(true);
    try {
      await EmployeeService(token).endPutOnLeave(confirmEndLoaData.employeeId, confirmEndLoaData.actualLastDay);
      toast.success('Saved successfully!');
      loadList();
      setConfirmEndLoaModalOpen(false);
      setConfirmEndLoaData(null);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Request error');
    } finally {
      setConfirmEndLoaLoading(false);
    }
  };

  const modal = (
    <>
      <PutOnLeaveForm
        isPutOnLeaveFormOpen={isPutOnLeaveFormOpen}
        tooglePutOnLeaveForm={tooglePutOnLeaveForm}
        handleSubmitPutOnLeave={handleSubmitPutOnLeave}
        putOnLeaveStep={putOnLeaveStep}
        loaUser={loaUser}
        firstDayOfLoa={firstDayOfLoa}
        setFirstDayOfLoa={setFirstDayOfLoa}
        lastDayOfLoa={lastDayOfLoa}
        setLastDayOfLoa={setLastDayOfLoa}
        typeOfLoa={typeOfLoa}
        setTypeOfLoa={setTypeOfLoa}
        selectedLoaPolicy={selectedLoaPolicy}
        setSelectedLoaPolicy={setSelectedLoaPolicy}
        selectedLoaReason={selectedLoaReason}
        setSelectedLoaReason={setSelectedLoaReason}
        reasonForLoa={reasonForLoa}
        setReasonForLoa={setReasonForLoa}
        documents={documents}
        setDocuments={setDocuments}
        additionalNotes={additionalNotes}
        setAdditionalNotes={setAdditionalNotes}
        setPutOnLeaveStep={setPutOnLeaveStep}
      />
      <LeaveOfAbsenceRecordsModal
        isOpen={confirmEndLoaModalOpen}
        toggle={() => {
          setConfirmEndLoaModalOpen(false);
          setConfirmEndLoaData(null);
        }}
        workerName={confirmEndLoaData?.workerName}
        loaRecords={confirmEndLoaData?.loaRecords ?? []}
        mode="confirmEnd"
        actualLastDay={confirmEndLoaData?.actualLastDay}
        onConfirmEnd={handleConfirmEndLoa}
        confirmLoading={confirmEndLoaLoading}
      />
    </>
  );

  return {
    modal,
    handlePutOnLeave,
    handleEndPutOnLeave,
  };
};

export default usePutOnLeaveModal;
